import React, { useState } from 'react'
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Eye, Settings, Plus, Copy, Trash2, Star } from 'lucide-react'
import { useViewSettings, DomainView } from '@/hooks/useViewSettings'
import { useToast } from '@/hooks/use-toast'

interface ViewSelectorProps {
  onViewChange?: (view: DomainView) => void
}

export function ViewSelector({ onViewChange }: ViewSelectorProps) {
  const { views, currentView, currentViewId, setCurrentViewId, createView, updateView, deleteView, setDefaultView, duplicateView } = useViewSettings()
  const { toast } = useToast()
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [showManageDialog, setShowManageDialog] = useState(false)
  const [newViewName, setNewViewName] = useState('')
  const [isCreating, setIsCreating] = useState(false)

  const handleViewChange = (viewId: string) => {
    setCurrentViewId(viewId)
    const selectedView = views.find(view => view.id === viewId)
    if (selectedView && onViewChange) {
      onViewChange(selectedView)
    }
  }

  const handleCreateView = async () => {
    if (!newViewName.trim()) return
    
    setIsCreating(true)
    try {
      // Create view with current settings
      const newViewId = createView(newViewName, {
        filters: currentView.filters,
        visibleColumns: currentView.visibleColumns,
        searchQuery: currentView.searchQuery,
        statusFilter: currentView.statusFilter,
        clientFilter: currentView.clientFilter
      })
      
      toast({
        title: "View created",
        description: `"${newViewName}" has been created and applied.`
      })
      
      setNewViewName('')
      setShowCreateDialog(false)
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create view",
        variant: "destructive"
      })
    } finally {
      setIsCreating(false)
    }
  }

  const handleDuplicateView = (view: DomainView) => {
    const newName = `${view.name} (Copy)`
    duplicateView(view.id, newName)
    toast({
      title: "View duplicated",
      description: `"${newName}" has been created.`
    })
  }

  const handleDeleteView = (viewId: string, viewName: string) => {
    if (viewId === 'default') return
    
    deleteView(viewId)
    toast({
      title: "View deleted",
      description: `"${viewName}" has been deleted.`
    })
  }

  const handleSetDefault = (viewId: string, viewName: string) => {
    setDefaultView(viewId)
    toast({
      title: "Default view updated",
      description: `"${viewName}" is now the default view.`
    })
  }

  return (
    <>
      <div className="flex items-center gap-2">
        <Select value={currentViewId} onValueChange={handleViewChange}>
          <SelectTrigger className="w-48">
            <div className="flex items-center gap-2">
              <Eye className="h-4 w-4" />
              <SelectValue />
            </div>
          </SelectTrigger>
          <SelectContent>
            {views.map((view) => (
              <SelectItem key={view.id} value={view.id}>
                <div className="flex items-center gap-2">
                  <span>{view.name}</span>
                  {view.isDefault && <Star className="h-3 w-3 fill-current" />}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <Settings className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setShowCreateDialog(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Create New View
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setShowManageDialog(true)}>
              <Settings className="mr-2 h-4 w-4" />
              Manage Views
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Create View Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Create New View</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="view-name" className="text-right">
                Name
              </Label>
              <Input
                id="view-name"
                value={newViewName}
                onChange={(e) => setNewViewName(e.target.value)}
                className="col-span-3"
                placeholder="Enter view name..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateView()
                  }
                }}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateView} disabled={!newViewName.trim() || isCreating}>
              {isCreating ? "Creating..." : "Create View"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage Views Dialog */}
      <Dialog open={showManageDialog} onOpenChange={setShowManageDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Manage Views</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {views.map((view) => (
              <div key={view.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{view.name}</span>
                      {view.isDefault && <Star className="h-4 w-4 fill-current text-yellow-500" />}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {view.filters.length} filters • {view.visibleColumns.length} columns
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!view.isDefault && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSetDefault(view.id, view.name)}
                    >
                      <Star className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDuplicateView(view)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  {view.id !== 'default' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteView(view.id, view.name)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}